import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submitButton"]

  connect() {
    this.pendingUploads = 0
    this.uploadErrors = []
    this.formSubmitRequested = false

    // Bind event handlers once so we can remove them later
    this.boundHandleUploadInitialize = this.handleUploadInitialize.bind(this)
    this.boundHandleUploadStart = this.handleUploadStart.bind(this)
    this.boundHandleUploadProgress = this.handleUploadProgress.bind(this)
    this.boundHandleUploadError = this.handleUploadError.bind(this)
    this.boundHandleUploadEnd = this.handleUploadEnd.bind(this)
    this.boundHandleAllUploadsEnd = this.handleAllUploadsEnd.bind(this)

    // Listen for ActiveStorage direct upload events
    this.element.addEventListener("direct-upload:initialize", this.boundHandleUploadInitialize)
    this.element.addEventListener("direct-upload:start", this.boundHandleUploadStart)
    this.element.addEventListener("direct-upload:progress", this.boundHandleUploadProgress)
    this.element.addEventListener("direct-upload:error", this.boundHandleUploadError)
    this.element.addEventListener("direct-upload:end", this.boundHandleUploadEnd)
    this.element.addEventListener("direct-uploads:end", this.boundHandleAllUploadsEnd)
  }

  disconnect() {
    this.element.removeEventListener("direct-upload:initialize", this.boundHandleUploadInitialize)
    this.element.removeEventListener("direct-upload:start", this.boundHandleUploadStart)
    this.element.removeEventListener("direct-upload:progress", this.boundHandleUploadProgress)
    this.element.removeEventListener("direct-upload:error", this.boundHandleUploadError)
    this.element.removeEventListener("direct-upload:end", this.boundHandleUploadEnd)
    this.element.removeEventListener("direct-uploads:end", this.boundHandleAllUploadsEnd)
  }

  handleUploadInitialize(_event) {
    this.pendingUploads++
    console.log(`[DirectUpload] Initialize: ${this.pendingUploads} pending`)
  }

  handleUploadStart(_event) {
    // Show overlay when first upload starts
    this.showOverlay("Uploading images...")
  }

  handleUploadProgress(_event) {
    // Optionally update overlay with progress
  }

  handleUploadError(event) {
    // Note: Don't decrement pendingUploads here - direct-upload:end fires
    // for ALL uploads (success or failure), so handleUploadEnd handles the decrement
    const { error, file } = event.detail
    this.uploadErrors.push({ file: file.name, error })
    console.error(`[DirectUpload] Error uploading ${file.name}:`, error)
  }

  handleUploadEnd(_event) {
    this.pendingUploads--
    // Guard against underflow from unexpected event sequences
    if (this.pendingUploads < 0) {
      console.warn("[DirectUpload] pendingUploads underflow; resetting to 0")
      this.pendingUploads = 0
    }
    console.log(`[DirectUpload] End: ${this.pendingUploads} pending`)
  }

  handleAllUploadsEnd(_event) {
    console.log("[DirectUpload] All uploads complete")

    if (this.uploadErrors.length > 0) {
      this.hideOverlay()
      this.formSubmitRequested = false // Reset so retry doesn't auto-submit
      alert(`Some uploads failed: ${this.uploadErrors.map(e => e.file).join(", ")}`)
      this.uploadErrors = []
      return
    }

    // Do not submit here. ActiveStorage's direct-upload pipeline already
    // resubmits the form once uploads finish. Calling form.submit() here was
    // causing duplicate POSTs (two training datasets per click).
    this.formSubmitRequested = false
    this.showOverlay("Processing dataset...")
  }

  handleSubmit(event) {
    // If uploads are still in progress, prevent submit and wait
    if (this.pendingUploads > 0) {
      event.preventDefault()
      this.formSubmitRequested = true
      this.showOverlay(`Uploading images (${this.pendingUploads} remaining)...`)
      console.log(`[DirectUpload] Submit blocked: ${this.pendingUploads} uploads pending`)
      return
    }

    // All uploads complete, show processing overlay
    this.showOverlay("Processing dataset...")
    // Allow form to submit normally
  }

  showOverlay(message) {
    // Use the existing generation-overlay controller
    const overlay = document.querySelector("[data-generation-overlay-target='container']")
    if (overlay) {
      const messageEl = overlay.querySelector("h3")
      if (messageEl && message) {
        messageEl.textContent = message
      }
      overlay.classList.remove("hidden")
    }
  }

  hideOverlay() {
    const overlay = document.querySelector("[data-generation-overlay-target='container']")
    if (overlay) {
      overlay.classList.add("hidden")
    }
  }
}
